<template>
  <!-- Link finding(s) to a component. Approved showcase: ai-analysis-tag-picker-showcase.html, option B (Khalid, 2026-09-07).
       The list is paged from the server (50 at a time) and searched server-side, so a facility with thousands of
       components costs the same as one with a few hundred. -->
  <v-dialog :value="value" max-width="560" scrollable :retain-focus="false" @input="onInput" @keydown.esc="close">
    <!-- No role here: v-dialog's content element already is the role="dialog" / aria-modal node. -->
    <section class="cp" aria-labelledby="cpTitle">
      <header class="cp__head">
        <h2 class="cp__title" id="cpTitle">Link to a component</h2>
        <v-btn icon small class="cp__close" aria-label="Close" @click="close"><v-icon :size="20">mdi-close</v-icon></v-btn>
        <div class="cp__who">
          <template v-if="single">
            <div class="cp__who-row cp__who-title">
              <span class="cp__badge" aria-hidden="true">{{ finding.badgeIndex }}</span>
              <b>Finding #{{ finding.badgeIndex }}<template v-if="finding.friendlyId && String(finding.friendlyId) !== String(finding.badgeIndex)"> · {{ finding.friendlyId }}</template></b>
            </div>
            <div class="cp__who-row cp__who-meta cp__who-scan" :title="scanName || 'Scan unavailable'">
              <v-icon :size="14" class="cp__who-icon">mdi-image-outline</v-icon>
              <span class="cp__who-label">Scan:</span>
              <span class="cp__who-val">{{ scanName || 'Scan unavailable' }}</span>
            </div>
            <div class="cp__who-row cp__who-meta cp__who-loc" :title="location || 'Location not recorded'">
              <v-icon :size="14" class="cp__who-icon">mdi-map-marker-outline</v-icon>
              <span class="cp__who-label">Location:</span>
              <span class="cp__who-val">{{ location || 'Location not recorded' }}</span>
            </div>
          </template>
          <template v-else>
            <div class="cp__who-row cp__who-title">
              <b>{{ findings.length }} findings selected</b>
            </div>
            <div v-if="alreadyLinked" class="cp__who-row cp__who-meta">
              <span class="cp__loc">{{ alreadyLinked }} already linked · the chosen component replaces those links</span>
            </div>
          </template>
        </div>
      </header>

      <!-- The app's tab pattern (.tag-info-tabs, _component-patterns.scss): a pill track with elevated segments, no slider. -->
      <div class="cp__tabs-wrap">
        <v-tabs :value="typeIndex" class="tag-info-tabs cp__tabs" :mobile-breakpoint="0" grow aria-label="Component type" @change="setTypeIndex">
          <v-tab v-for="tab in tabs" :key="tab.key" :aria-label="tab.label">
            <v-icon :size="16" class="cp__tab-icon">{{ tab.icon }}</v-icon>
            <span class="cp__tab-label">{{ tab.label }}</span>
          </v-tab>
        </v-tabs>
      </div>

      <label class="cp__search">
        <v-icon :size="18" class="cp__search-icon">mdi-magnify</v-icon>
        <input
          ref="search"
          v-model="search"
          type="text"
          :placeholder="placeholder"
          :aria-label="placeholder"
          autocomplete="off"
          spellcheck="false"
          @input="onSearch"
          @keydown="onKeydown" />
        <v-btn v-if="search" icon x-small class="cp__clear" aria-label="Clear search" @click="clearSearch"><v-icon :size="16">mdi-close</v-icon></v-btn>
      </label>

      <div v-if="recentOptions.length" class="cp__recent">
        <span class="cp__recent-lbl">Recent</span>
        <button v-for="option in recentOptions" :key="option.id" type="button" class="cp__recent-chip" :title="chipTitle(option)" @click="pick(option)" @dblclick="apply(option)">
          <v-icon :size="14" class="cp__ticon" :class="typeClass(option)">{{ icon(option) }}</v-icon>
          <span>{{ option.name }}</span>
        </button>
      </div>

      <p class="cp__status" aria-live="polite">{{ statusText }}</p>

      <div class="cp__list-wrap">
        <div ref="list" class="cp__list" role="listbox" @scroll="onScroll">
          <button
            v-if="showSuggested"
            type="button"
            class="cp__item cp__item--suggest"
            :class="{ 'is-picked': isPicked(suggested) }"
            role="option"
            :aria-selected="String(isPicked(suggested))"
            @click="pick(suggested)"
            @dblclick="apply(suggested)">
            <v-icon :size="18" class="cp__ticon" :class="typeClass(suggested)">{{ icon(suggested) }}</v-icon>
            <span class="cp__name">{{ suggested.name }}</span>
            <span class="cp__type cp__type--suggest">Suggested · {{ suggestedFrom }}</span>
            <v-icon :size="18" class="cp__chk" aria-hidden="true">mdi-check</v-icon>
          </button>

          <div v-if="errorText" class="cp__empty" role="alert">
            <v-icon :size="26" class="cp__empty-icon">mdi-alert-circle-outline</v-icon>
            {{ errorText }}
            <v-btn small outlined class="cp__empty-btn" @click="retryFailedLoad">Try again</v-btn>
          </div>
          <div v-else-if="total === 0" class="cp__empty">
            <v-icon :size="26" class="cp__empty-icon">mdi-magnify-close</v-icon>
            No component matches “{{ search.trim() }}”{{ type ? ` in ${type}` : '' }}.
            <v-btn small outlined class="cp__empty-btn" @click="clearSearch">Clear search</v-btn>
          </div>
          <template v-else>
            <button
              v-for="(option, i) in items"
              :key="option.id"
              type="button"
              class="cp__item"
              :title="option.name"
              :class="{ 'is-hi': i === hi, 'is-current': option.id === currentId, 'is-picked': isPicked(option) && option.id !== currentId }"
              role="option"
              :aria-selected="String(isPicked(option))"
              :data-i="i"
              @click="pick(option, i)"
              @dblclick="apply(option)">
              <v-icon :size="18" class="cp__ticon" :class="typeClass(option)">{{ icon(option) }}</v-icon>
              <span class="cp__name">{{ option.name }}</span>
              <span class="cp__type">{{ option.type || '' }}</span>
              <v-icon :size="18" class="cp__chk" aria-hidden="true">mdi-check</v-icon>
            </button>
            <div v-if="loading" class="cp__skel" aria-hidden="true"><i></i><i></i><i></i></div>
            <button v-else-if="hasMore" type="button" class="cp__more" @click="loadMore">Show more</button>
          </template>
        </div>
      </div>

      <footer class="cp__foot">
        <v-btn v-if="canUnlink" small text color="error" class="cp__foot-btn" :disabled="busy" @click="$emit('unlink')">
          <v-icon :size="16" left>mdi-link-variant-off</v-icon>
          Remove link
        </v-btn>
        <span v-if="pendingPick" class="cp__sel">
          <v-icon :size="16" class="cp__ticon" :class="typeClass(pendingPick)">{{ icon(pendingPick) }}</v-icon>
          <span class="cp__sel-name">{{ pendingPick.name }}</span>
        </span>
        <span v-else class="cp__sel cp__sel--none">Choose a component, then Link · double-click links at once</span>
        <span class="cp__spacer"></span>
        <v-btn small text class="cp__foot-btn" @click="close">Cancel</v-btn>
        <v-btn small depressed color="primary" class="cp__foot-btn" :disabled="!pendingPick || busy" :loading="busy" @click="apply(pendingPick)">
          <v-icon :size="16" left>mdi-link-variant</v-icon>
          Link
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script>
  import baseTagService from '@Tags/services/baseTag.service';
  import { error } from '@/utils/debugLogger';
  import { EM_DASH } from '../../utils/reportFormat';
  import { COMPONENT_TYPES, buildComponentQuery, componentOfFinding, componentPageOf, componentTypeIcon, linkedCount } from '../../utils/findingComponents';
  import { COMPONENT_SEARCH_PLACEHOLDER, COMPONENT_NO_ACCESS, COMPONENT_LOAD_FAILED, SUGGESTED_FROM } from './reportConstants';

  const SEARCH_DEBOUNCE_MS = 250;
  const LOAD_MORE_THRESHOLD_PX = 120;
  const KEYBOARD_PREFETCH_ROWS = 5;
  const REQUEST_TIMEOUT_MS = 60000;
  const FORBIDDEN = 403;

  export default {
    name: 'ComponentPickerDialog',
    props: {
      value: { type: Boolean, default: false },
      /** The finding(s) being linked; one opens the single header, several the bulk header. */
      findings: { type: Array, default: () => [] },
      facilityId: { type: String, required: true },
      /** The AVA Inspect's own component ({ id, name, type }) for AVA Inspect jobs; shown as a suggestion, never applied silently. */
      suggested: { type: Object, default: null },
      /** Components linked earlier in this report, newest first. */
      recent: { type: Array, default: () => [] },
      busy: { type: Boolean, default: false },
    },
    data() {
      return {
        search: '',
        type: COMPONENT_TYPES[0].key,
        items: [],
        total: null,
        page: 0,
        loading: false,
        hi: 0,
        picked: null,
        errorText: '',
        searchTimer: null,
        requestTimeoutMs: REQUEST_TIMEOUT_MS,
        cacheGeneration: 0,
        cacheByKey: new Map(),
        inFlightByPage: new Map(),
        failedLoad: null,
        placeholder: COMPONENT_SEARCH_PLACEHOLDER,
        suggestedFrom: SUGGESTED_FROM,
        tabs: [...COMPONENT_TYPES],
      };
    },
    computed: {
      single() {
        return this.findings.length === 1;
      },
      finding() {
        return this.findings[0] || {};
      },
      scanName() {
        return this.finding.scanName || this.finding.imageName || '';
      },
      location() {
        return this.finding.locationDescription || this.finding.location || '';
      },
      /** The single finding's current component id; the bulk header has no single "current". */
      currentId() {
        const component = this.single ? componentOfFinding(this.finding) : null;
        return component ? component.id : null;
      },
      alreadyLinked() {
        return linkedCount(this.findings);
      },
      canUnlink() {
        return this.alreadyLinked > 0;
      },
      pendingPick() {
        return this.picked && this.picked.id !== this.currentId ? this.picked : null;
      },
      hasMore() {
        return this.total != null && this.items.length < this.total;
      },
      /** v-tabs works on the tab index; the picker works on the type key. */
      typeIndex() {
        return Math.max(0, this.tabs.findIndex((tab) => tab.key === this.type));
      },
      showSuggested() {
        return !!this.suggested && this.suggested.type === this.type && !this.search.trim() && this.total != null && !this.errorText;
      },
      recentOptions() {
        if (this.search.trim()) return [];
        return this.recent.filter((c) => c && c.type === this.type && c.id !== this.currentId);
      },
      statusText() {
        if (this.errorText) return '';
        if (this.total == null) return 'Searching…';
        if (this.total === 0) return '';
        return this.hasMore ? 'Scroll for more' : 'All matching components loaded';
      },
    },
    watch: {
      value(open) {
        if (open) this.reset();
        else clearTimeout(this.searchTimer);
      },
      facilityId(nextFacilityId, previousFacilityId) {
        if (nextFacilityId === previousFacilityId) return;
        clearTimeout(this.searchTimer);
        this.cacheGeneration += 1;
        this.cacheByKey.clear();
        this.inFlightByPage.clear();
        this.clearFailedLoad();
        this.search = '';
        this.type = COMPONENT_TYPES[0].key;
        if (this.value) this.reset();
      },
    },
    beforeDestroy() {
      clearTimeout(this.searchTimer);
    },
    methods: {
      reset() {
        clearTimeout(this.searchTimer);
        this.search = '';
        this.type = COMPONENT_TYPES[0].key;
        this.picked = this.single ? componentOfFinding(this.finding) : null;
        const load = this.loadFirst();
        this.$nextTick(() => this.focusSearch());
        return load;
      },
      focusSearch() {
        const input = this.$refs.search;
        if (input && document.activeElement !== input) input.focus();
      },
      onInput(open) {
        this.$emit('input', open);
      },
      close() {
        this.$emit('input', false);
      },
      icon(component) {
        return componentTypeIcon(component && component.type);
      },
      typeClass(component) {
        return component && component.type ? `t-${component.type}` : '';
      },
      chipTitle(component) {
        return `${component.name} · ${component.type || 'Component'}`;
      },
      isPicked(component) {
        return !!component && !!this.picked && this.picked.id === component.id;
      },
      query(page) {
        return buildComponentQuery({ facilityId: this.facilityId, search: this.search, type: this.type, page });
      },
      cacheKey(query = this.query(1)) {
        return JSON.stringify([query.assetId, query.tagTypes[0], query.search || '']);
      },
      isActiveCacheKey(key) {
        return key === this.cacheKey();
      },
      restoreCached(cached) {
        this.items = [...cached.items];
        this.total = cached.total;
        this.page = cached.page;
        this.clearFailedLoad();
      },
      async fetchPage(query) {
        const { data } = await baseTagService.typeHead(query);
        return componentPageOf(data);
      },
      requestPage(query, generation) {
        const requestKey = JSON.stringify([generation, this.cacheKey(query), query.page]);
        const pending = this.inFlightByPage.get(requestKey);
        if (pending) return pending.promise;
        const record = { promise: null };
        let timeoutId;
        const timeout = new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Component request timed out')), this.requestTimeoutMs);
        });
        record.promise = Promise.race([this.fetchPage(query), timeout])
          .finally(() => {
            clearTimeout(timeoutId);
            if (this.inFlightByPage.get(requestKey) === record) this.inFlightByPage.delete(requestKey);
          });
        this.inFlightByPage.set(requestKey, record);
        return record.promise;
      },
      async loadFirst() {
        const query = this.query(1);
        const key = this.cacheKey(query);
        const generation = this.cacheGeneration;
        const cached = this.cacheByKey.get(key);
        this.hi = 0;
        this.errorText = '';
        if (cached) {
          this.restoreCached(cached);
          this.loading = false;
          return;
        }
        this.items = [];
        this.total = null;
        this.page = 0;
        this.loading = true;
        try {
          const page = await this.requestPage(query, generation);
          if (generation !== this.cacheGeneration) return;
          const loaded = { items: page.items, total: page.total, page: 1 };
          this.cacheByKey.set(key, loaded);
          if (this.isActiveCacheKey(key)) this.restoreCached(loaded);
        } catch (err) {
          if (generation === this.cacheGeneration && this.isActiveCacheKey(key)) this.fail(err, key, 1);
        } finally {
          if (generation === this.cacheGeneration && this.isActiveCacheKey(key)) this.loading = false;
        }
      },
      async loadMore() {
        if (this.loading || !this.hasMore) return;
        const query = this.query(this.page + 1);
        const key = this.cacheKey(query);
        const generation = this.cacheGeneration;
        const targetPage = query.page;
        const starting = this.cacheByKey.get(key) || { items: [...this.items], total: this.total, page: this.page };
        this.loading = true;
        try {
          const page = await this.requestPage(query, generation);
          if (generation !== this.cacheGeneration) return;
          const latest = this.cacheByKey.get(key);
          if (latest && latest.page >= targetPage) {
            if (this.isActiveCacheKey(key)) this.restoreCached(latest);
            return;
          }
          const base = latest && latest.page === targetPage - 1 ? latest : starting;
          const loaded = { items: [...base.items, ...page.items], total: page.total, page: targetPage };
          this.cacheByKey.set(key, loaded);
          if (this.isActiveCacheKey(key)) this.restoreCached(loaded);
        } catch (err) {
          if (generation === this.cacheGeneration && this.isActiveCacheKey(key)) this.fail(err, key, targetPage);
        } finally {
          if (generation === this.cacheGeneration && this.isActiveCacheKey(key)) this.loading = false;
        }
      },
      clearFailedLoad() {
        this.failedLoad = null;
        this.errorText = '';
      },
      fail(err, key, page) {
        error('[ComponentPickerDialog] component list failed', err);
        const status = err && err.response && err.response.status;
        this.errorText = status === FORBIDDEN ? COMPONENT_NO_ACCESS : COMPONENT_LOAD_FAILED;
        this.failedLoad = { key, page };
        this.total = null;
      },
      retryFailedLoad() {
        const failed = this.failedLoad;
        const key = this.cacheKey();
        this.clearFailedLoad();
        if (!failed || failed.key !== key || failed.page === 1) return this.loadFirst();
        const cached = this.cacheByKey.get(key);
        if (!cached) return this.loadFirst();
        this.restoreCached(cached);
        return this.loadMore();
      },
      onSearch() {
        clearTimeout(this.searchTimer);
        this.clearFailedLoad();
        this.searchTimer = setTimeout(() => this.loadFirst(), SEARCH_DEBOUNCE_MS);
      },
      clearSearch() {
        clearTimeout(this.searchTimer);
        this.clearFailedLoad();
        this.search = '';
        this.loadFirst();
        this.focusSearch();
      },
      setType(key) {
        if (this.type === key) return;
        clearTimeout(this.searchTimer);
        this.clearFailedLoad();
        this.type = key;
        this.loadFirst();
        this.focusSearch();
      },
      setTypeIndex(index) {
        this.setType((this.tabs[index] || COMPONENT_TYPES[0]).key);
      },
      onScroll() {
        const list = this.$refs.list;
        if (list && list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_MORE_THRESHOLD_PX) this.loadMore();
      },
      onKeydown(event) {
        const count = this.items.length;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.hi = Math.min(count - 1, this.hi + 1);
          if (this.hi >= count - KEYBOARD_PREFETCH_ROWS) this.loadMore();
          this.revealHighlight();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.hi = Math.max(0, this.hi - 1);
          this.revealHighlight();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (event.ctrlKey || event.metaKey) {
            if (this.pendingPick) this.apply(this.pendingPick);
            return;
          }
          const option = this.items[this.hi];
          if (option) this.pick(option, this.hi);
        }
      },
      revealHighlight() {
        this.$nextTick(() => {
          const node = this.$refs.list && this.$refs.list.querySelector('.cp__item.is-hi');
          if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
        });
      },
      pick(component, index) {
        if (Number.isInteger(index)) this.hi = index;
        this.picked = component;
      },
      apply(component) {
        if (!component || this.busy) return;
        this.$emit('link', component);
      },
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';

  // Unscoped: v-dialog teleports the content to the app root, so the tokens are included on the dialog root itself.
  // Every selector is rooted at .cp.
  .cp {
    @include ai-analysis-tokens;
    --cp-type-equipment: #215f9b;
    --cp-type-pipeline: var(--aa-brand);
    --cp-type-structural: #6d4c9b;

    display: flex;
    flex-direction: column;
    height: min(600px, calc(90vh - 16px));
    max-height: calc(90vh - 16px);
    overflow: hidden;
    background: var(--aa-surface);
    color: var(--aa-text-primary);
    font-family: var(--ds-font-family-body);

    // Vuetify's global `.v-application p { margin-bottom: 16px }` outranks a single class; pin both paragraphs here.
    & .cp__who, & .cp__status { margin: 0; }
    &__head,
    &__tabs-wrap,
    &__search,
    &__recent,
    &__status,
    &__foot { flex: 0 0 auto; }

    &__head {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 8px var(--ds-space-sm);
      padding: 14px var(--ds-space-md) 10px;
    }
    &__title { margin: 0; font-family: var(--ds-font-family-heading) !important; font-size: var(--ds-font-subtitle); font-weight: 600; }
    &__close { margin: -4px -4px 0 0; color: var(--aa-text-secondary) !important; }
    &__who {
      grid-column: 1 / -1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      min-width: 0;
      font-size: var(--ds-font-body-sm);
      line-height: 20px;
      color: var(--aa-text-muted);
    }
    &__who-row {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      max-width: 100%;
    }
    &__who-title {
      gap: var(--ds-space-sm);
      b {
        color: var(--aa-text-primary);
        font-weight: 600;
        white-space: nowrap;
      }
    }
    &__who-meta {
      font-size: var(--ds-font-caption);
      color: var(--aa-text-secondary);
    }
    &__who-icon {
      flex: 0 0 auto;
      color: var(--aa-text-muted) !important;
    }
    &__who-label {
      flex: 0 0 auto;
      font-weight: 500;
      color: var(--aa-text-muted);
    }
    &__who-val {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--aa-text-primary);
    }
    &__who-loc .cp__who-val {
      text-transform: capitalize;
    }
    &__badge { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; min-width: 22px; height: 20px; padding: 0 5px; border-radius: var(--ds-radius-sm); background: var(--aa-badge); color: #fff; font-family: var(--ds-font-family-mono) !important; font-size: var(--aa-font-micro); font-weight: 700; line-height: 1; }
    &__loc { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    &__tabs-wrap { padding: 0 var(--ds-space-md); }
    // The shared .tag-info-tabs pattern sizes its icons for icon-only tabs (24px); these tabs carry a label.
    & .tag-info-tabs.cp__tabs {
      margin: 0;
      .v-tabs-bar { height: 52px !important; }
      .v-slide-group__wrapper { margin: 0; padding: 6px; }
      .v-tab { gap: 6px; height: 40px !important; font-weight: 500; font-size: 14px; }
      .v-tab .v-icon { font-size: 16px !important; }
    }
    &__tab-icon { flex: 0 0 auto; }
    &__tab-label { line-height: 1; }

    &__search {
      position: relative;
      display: flex;
      align-items: center;
      margin: 10px var(--ds-space-md) 6px;

      input {
        display: block;
        flex: 1 1 auto;
        width: 100%;
        height: 36px;
        padding: 0 34px;
        border: 1px solid var(--aa-border-strong);
        border-radius: var(--ds-radius-sm);
        background: var(--aa-surface);
        color: var(--aa-text-primary);
        font: inherit;
        font-size: var(--ds-font-body-sm);
        caret-color: var(--aa-brand);
        &::placeholder { color: var(--aa-text-muted); }
        &:focus { outline: none; border-color: var(--aa-brand); box-shadow: 0 0 0 2px var(--aa-brand-soft); }
      }
    }
    // A flex sibling laid over the input's left padding: Vuetify's .v-icon rules fight an absolute + translate placement.
    &__search-icon { flex: 0 0 auto; z-index: 1; margin: 0 -28px 0 10px; color: var(--aa-text-muted) !important; pointer-events: none; }
    &__clear { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); color: var(--aa-text-muted) !important; }

    &__recent { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; padding: 2px var(--ds-space-md) 8px; }
    &__recent-lbl { margin-right: 2px; color: var(--aa-text-muted); font-size: var(--aa-font-micro); font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
    &__recent-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      max-width: 220px;
      height: 24px;
      padding: 0 8px 0 6px;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      background: var(--aa-surface-subtle);
      color: var(--aa-text-primary);
      font-family: var(--ds-font-family-mono) !important;
      font-size: var(--aa-font-micro);
      cursor: pointer;
      span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      &:hover { border-color: var(--aa-brand); background: var(--aa-brand-soft); }
      &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 1px; }
    }

    &__status { min-height: 18px; padding: 0 var(--ds-space-md) 6px; color: var(--aa-text-muted); font-size: var(--aa-font-micro); line-height: 18px; letter-spacing: 0.02em; }

    &__list-wrap {
      flex: 1 1 auto;
      min-height: 0;
      margin: 0 var(--ds-space-md) 10px;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      background: var(--aa-surface);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    &__list { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
    &__item {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 38px;
      padding: 6px var(--ds-space-md);
      border: none;
      border-bottom: 1px solid var(--aa-border-subtle);
      background: transparent;
      color: var(--aa-text-primary);
      font: inherit;
      text-align: left;
      cursor: pointer;

      &:hover, &.is-hi { background: var(--aa-surface-hover); }
      &.is-current { background: var(--aa-surface-active); }
      &.is-picked { background: var(--aa-surface-active); }
      &.is-picked .cp__name { font-weight: 600; }
      &.is-current .cp__chk, &.is-picked .cp__chk { visibility: visible; }
      &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: -2px; }
      &:last-child { border-bottom: none; }
    }
    &__item--suggest { background: var(--aa-brand-soft); border-bottom-color: var(--aa-border-default); }
    &__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-body-sm); }
    &__type { flex: 0 0 auto; color: var(--aa-text-muted); font-size: var(--aa-font-micro); letter-spacing: 0.04em; text-transform: uppercase; }
    &__type--suggest { color: var(--aa-brand); text-transform: none; letter-spacing: 0; font-weight: 600; }
    &__chk { flex: 0 0 auto; color: var(--aa-brand) !important; visibility: hidden; }
    &__ticon { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; color: var(--aa-text-secondary) !important; }
    .t-Equipment { color: var(--cp-type-equipment) !important; }
    .t-Pipeline { color: var(--cp-type-pipeline) !important; }
    .t-Structural { color: var(--cp-type-structural) !important; }

    &__skel { display: grid; gap: 10px; padding: 8px var(--ds-space-md); }
    &__skel i { display: block; height: 14px; border-radius: 3px; background: var(--aa-surface-hover); }
    &__skel i:nth-child(2) { width: 72%; }
    &__skel i:nth-child(3) { width: 84%; }
    &__more { display: block; width: 100%; padding: 8px var(--ds-space-md); border: none; background: var(--aa-surface-subtle); color: var(--aa-brand); font: inherit; font-size: var(--ds-font-caption); font-weight: 600; text-align: center; cursor: pointer; }
    &__empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px 16px; color: var(--aa-text-muted); font-size: var(--ds-font-body-sm); line-height: 1.6; text-align: center; }
    &__empty-icon { color: var(--aa-text-disabled) !important; }
    &__empty-btn { text-transform: none; letter-spacing: 0; }

    &__foot {
      display: flex;
      align-items: center;
      gap: var(--ds-space-sm);
      padding: 10px var(--ds-space-md);
      border-top: 1px solid var(--aa-border-subtle);
      background: var(--aa-surface-subtle);
      font-size: var(--ds-font-caption);
      color: var(--aa-text-muted);
    }
    &__foot-btn { text-transform: none; letter-spacing: 0.02em; }
    &__spacer { flex: 1 1 auto; }
    &__sel { display: inline-flex; align-items: center; gap: 6px; min-width: 0; color: var(--aa-text-primary); font-size: var(--ds-font-caption); }
    &__sel-name { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--ds-font-family-mono) !important; }
    &__sel--none { color: var(--aa-text-muted); }

    @media (max-width: 600px) {
      &__item { min-height: 44px; }
      &__name {
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
        overflow-wrap: anywhere;
        line-height: 18px;
      }
    }
  }

  .theme--dark .cp {
    @include ai-analysis-tokens-dark;
    --cp-type-equipment: #7fb3e6;
    --cp-type-structural: #b79cdc;
  }
</style>
